import type { ReactNode } from 'react';

import { useMemo } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { fNumber } from 'src/utils/format/format-number';
import { toPivotTable } from 'src/utils/pivot/pivot-shape';

import { RADIUS } from 'src/theme/ui-tokens';
import { findMetric } from 'src/constants/pivot-fields';
import { findChartKind } from 'src/constants/pivot-charts';
import { ALERT_CONTENT_TYPES } from 'src/constants/alert-rule';

import { Iconify } from 'src/components/iconify';
import { AnalyticsChart } from 'src/components/cmui/chart/pivot-chart';
import { TrackYearCard } from 'src/components/cmui/track/track-year-card';
import { HubChipRow, HubChipStatic } from 'src/components/cmui/hub/hub-chip';
import { AlertSlackPreview } from 'src/components/cmui/alert/alert-rule-preview';

import { CM } from '../constants/home-theme';
import { TRACK_FLOW } from '../constants/home-copy';
import { HomeSceneFrame } from './home-scene-frame';
import { TRACK_SET, TRACK_ALERT, TRACK_DAILY, TRACK_YEAR_MONTHS } from '../constants/home-scene-samples';

import type { HomeScene } from '../constants/home-copy';

// ----------------------------------------------------------------------

const COUNT_METRIC = findMetric('count')!;

const YEAR_TOTAL = TRACK_YEAR_MONTHS.reduce((sum, row) => sum + row.count, 0);

/** 알림 쪽지에 싣는 내용 — 요약 통계와 매칭 포스팅(설정 창의 같은 칸) */
const ALERT_CONTENT = { [ALERT_CONTENT_TYPES.STATISTICS]: true, [ALERT_CONTENT_TYPES.POSTING_LIST]: true };

/** 폰의 쪽지 — 채널 비중을 접고 글은 둘만(한 화면에 들도록) */
const PHONE_ALERT = { ...TRACK_ALERT, channels: [], posts: TRACK_ALERT.posts.slice(0, 2) };

// ----------------------------------------------------------------------

/**
 * 장면 4 · 추적 — 「등록 → 최근 1년치가 바로 → 새 글이 매일 쌓임 → Slack·이메일 알림」 흐름 넷.
 *
 * 🔑 칸마다 서비스 부품이다 — 추적 등록의 칩 줄(`HubChipRow`) · 등록 창의 요약 카드(`TrackYearCard`) ·
 *   추이 그림(`AnalyticsChart`) · 알림 설정 창의 Slack 미리보기(`AlertSlackPreview`). 값만 예시다.
 * 🔑 넓은 화면은 왼쪽에 1→2→3 이 세로로, 오른쪽에 알림 쪽지가 크게 선다 — 이 장면의 주인공은 넷째 칸이다.
 */
export function HomeSceneTracking({ scene }: { scene: HomeScene }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const daily = useMemo(() => toPivotTable(TRACK_DAILY), []);

  return (
    <HomeSceneFrame scene={scene} layout="stack">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          gap: { xs: 1.5, md: 4 },
          alignItems: 'stretch',
        }}
      >
        <Stack spacing={{ xs: 1.5, md: 3 }}>
          <FlowStep index={0} arrow="down">
            <Stack spacing={0.75}>
              <HubChipRow label={phone ? undefined : '키워드'}>
                {TRACK_SET.keywords.map((word) => (
                  <HubChipStatic key={word} label={word} />
                ))}
              </HubChipRow>
              {!phone && (
                <HubChipRow label="계정">
                  {TRACK_SET.accounts.map((account) => (
                    <HubChipStatic key={account} label={account} />
                  ))}
                </HubChipRow>
              )}
            </Stack>
          </FlowStep>

          <FlowStep index={1} arrow="down">
            <TrackYearCard type="keyword" year={{ state: 'ready', total: YEAR_TOTAL, months: TRACK_YEAR_MONTHS }} />
          </FlowStep>

          {/* 폰은 그림 대신 하루치 수 한 줄 — 넷째 칸(알림)이 한 화면 안에 들도록 */}
          <FlowStep index={2} arrow="right" note={phone ? `하루 ${fNumber(TRACK_ALERT.total)}건` : undefined}>
            {!phone && <AnalyticsChart table={daily} metric={COUNT_METRIC} kind={findChartKind('column')} height={120} legend={false} chartId="home-track-daily" />}
          </FlowStep>
        </Stack>

        <FlowStep index={3} highlight>
          <AlertSlackPreview content={ALERT_CONTENT} ruleName="PDRN 추적" sample={phone ? PHONE_ALERT : TRACK_ALERT} showLimit={false} />
        </FlowStep>
      </Box>
    </HomeSceneFrame>
  );
}

// ----------------------------------------------------------------------

/**
 * 흐름 한 칸 — 차례 번호 · 이름 · 내용. 넓은 화면은 다음 칸 쪽(`arrow`)에 화살표가 붙는다.
 * `highlight` 는 장면의 주인공 칸 — 파란 테두리와 옅은 바탕.
 */
function FlowStep({
  index,
  arrow,
  highlight = false,
  note,
  children,
}: {
  index: number;
  arrow?: 'down' | 'right';
  highlight?: boolean;
  /** 이름 줄 오른쪽 끝의 짧은 값 — 내용 대신 한 줄로 말할 때 */
  note?: string;
  children?: ReactNode;
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        p: { xs: 1.5, md: 2 },
        borderRadius: RADIUS.lg,
        border: `1px solid ${highlight ? CM.primary : CM.border}`,
        bgcolor: highlight ? CM.alpha(0.04) : '#fff',
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: children ? 1.25 : 0 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: RADIUS.pill,
            bgcolor: CM.primary,
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {index + 1}
        </Box>
        <Typography sx={{ flex: 1, fontSize: 15, fontWeight: 600, color: CM.textDark }}>{TRACK_FLOW[index]}</Typography>
        {note && <Typography sx={{ fontSize: 14, fontWeight: 700, color: CM.primary }}>{note}</Typography>}
      </Stack>
      {children}
      {arrow && (
        <Box
          aria-hidden
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'absolute',
            ...(arrow === 'down'
              ? { left: '50%', bottom: -22, transform: 'translateX(-50%)' }
              : { top: '50%', right: -28, transform: 'translateY(-50%)' }),
            width: 20,
            height: 20,
            color: CM.primary,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify icon={arrow === 'down' ? 'solar:alt-arrow-down-linear' : 'solar:arrow-right-linear'} width={18} />
        </Box>
      )}
    </Box>
  );
}
